import { useState } from "react";
import { Printer, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDT, fmtD, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Card, Table, Th, Td, Pill } from "@/components/kit";
import { ALLERGENS } from "@/pages/Allergens";
import { Md } from "@/pages/Haccp";
import { Photos } from "@/components/PhotoPicker";

const Section = ({ title, count, children }) => (
  <section className="mb-10 break-inside-avoid-page">
    <h2 className="mb-3 border-b-2 border-slate-900 pb-1 text-xl font-extrabold">{title} {count != null && <span className="font-mono text-sm font-normal text-slate-500">({count})</span>}</h2>
    {children}
  </section>
);

function Report({ d }) {
  return (
    <div data-testid="audit-pack-report" className="bg-white">
      <div className="mb-8 flex flex-wrap justify-between gap-4 border-b border-slate-200 pb-6">
        <div><div className="ka-eyebrow">Food safety audit pack</div><h1 className="text-3xl font-extrabold">{d.business} · {d.venue.name}</h1><p className="text-sm text-slate-600">{fmtD(d.date_from)} – {fmtD(d.date_to)}</p></div>
        <div className="text-right text-xs text-slate-500">Generated {fmtDT(d.generated_at)}<br />by {d.generated_by} · KitchenAudit.app</div>
      </div>
      <Section title="Daily checks" count={d.check_runs.length}>
        <Table><thead><tr><Th>Date</Th><Th>Check</Th><Th>Signed off</Th><Th>Result</Th><Th>Corrective action</Th></tr></thead><tbody>
          {d.check_runs.map((r) => <tr key={r.id}><Td>{fmtD(r.date)}</Td><Td>{r.template_name}</Td><Td>{r.completed_by} · {fmtDT(r.completed_at)}</Td><Td><Pill status={r.has_exception ? "exception" : "ok"} label={r.has_exception ? "Exception" : "Pass"} /></Td><Td className="text-xs">{r.corrective_action || "—"}<div className="mt-1"><Photos ids={r.photo_ids} size="h-14 w-14" /></div></Td></tr>)}
        </tbody></Table>
      </Section>
      <Section title="Temperature records" count={d.temp_logs.length}>
        <Table><thead><tr><Th>Measured</Th><Th>Item</Th><Th>Reading</Th><Th>By</Th><Th>Action / food</Th></tr></thead><tbody>
          {d.temp_logs.map((t) => <tr key={t.id}><Td className="font-mono text-xs">{fmtDT(t.measured_at)}</Td><Td>{t.equipment_name || t.item || t.kind}</Td><Td><Pill status={t.out_of_range ? "overdue" : "ok"} label={`${t.value}°C`} /></Td><Td>{t.recorded_by}</Td><Td className="text-xs">{t.action_taken ? `${t.action_taken} / ${t.food_disposition}` : "—"}<div className="mt-1"><Photos ids={t.photo_ids} size="h-14 w-14" /></div></Td></tr>)}
        </tbody></Table>
      </Section>
      <Section title="Incidents & corrective actions" count={d.incidents.length}>
        <Table><thead><tr><Th>Raised</Th><Th>Incident</Th><Th>Status</Th><Th>Action</Th><Th>Verification</Th></tr></thead><tbody>
          {d.incidents.map((i) => <tr key={i.id}><Td className="text-xs">{fmtDT(i.created_at)}</Td><Td>{i.title}</Td><Td><Pill status={i.status} /></Td><Td className="text-xs">{i.corrective_action || "—"}<br />{i.food_disposition}</Td><Td className="text-xs">{i.verification ? `${i.verification} (${i.verified_by})` : "—"}<div className="mt-1"><Photos ids={i.photo_ids} size="h-14 w-14" /></div></Td></tr>)}
        </tbody></Table>
      </Section>
      <Section title="Deliveries" count={d.deliveries.length}>
        <Table><thead><tr><Th>Received</Th><Th>Supplier</Th><Th>Items</Th><Th>Temp</Th><Th>Decision</Th></tr></thead><tbody>
          {d.deliveries.map((x) => <tr key={x.id}><Td className="text-xs">{fmtDT(x.received_at)}</Td><Td>{x.supplier_name}</Td><Td>{x.items}</Td><Td>{x.temperature ?? "—"}</Td><Td>{x.accepted ? "Accepted" : `Rejected: ${x.rejection_reason}`}<div className="mt-1"><Photos ids={x.photo_ids} size="h-14 w-14" /></div></Td></tr>)}
        </tbody></Table>
      </Section>
      <Section title="Staff training" count={d.training.length}>
        <Table><thead><tr><Th>Staff</Th><Th>Course</Th><Th>Completed</Th><Th>Expires</Th></tr></thead><tbody>
          {d.training.map((t) => <tr key={t.id}><Td>{t.staff_name}</Td><Td>{t.course}</Td><Td>{fmtD(t.completed_on)}</Td><Td>{t.expires_on ? fmtD(t.expires_on) : "—"}</Td></tr>)}
        </tbody></Table>
      </Section>
      <Section title="Allergen matrix" count={d.dishes.length}>
        <Table><thead><tr><Th>Dish</Th><Th>Allergens present</Th></tr></thead><tbody>
          {d.dishes.map((x) => <tr key={x.id}><Td>{x.name}</Td><Td>{ALLERGENS.filter((a) => x.allergens.includes(a)).join(", ") || "None of the 14"}</Td></tr>)}
        </tbody></Table>
      </Section>
      <Section title="Equipment register" count={d.equipment.length}>
        <Table><thead><tr><Th>Equipment</Th><Th>Type</Th><Th>Status</Th><Th>Service due</Th><Th>Services logged</Th></tr></thead><tbody>
          {d.equipment.map((e) => <tr key={e.id}><Td>{e.name}</Td><Td>{e.type}</Td><Td>{e.status}</Td><Td>{e.service_due ? fmtD(e.service_due) : "—"}</Td><Td>{(e.service_history || []).length}</Td></tr>)}
        </tbody></Table>
      </Section>
      {d.haccp.map((h) => <Section key={h.id} title={`HACCP plan: ${h.title} (v${h.version})`}><Md>{h.content}</Md></Section>)}
    </div>
  );
}

export default function AuditPack() {
  const { venueId, venue } = useAuth();
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const build = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: d } = await api.get("/reports/audit-pack", { params: { venue_id: venueId, date_from: from, date_to: to } });
      setData(d);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="no-print">
        <PageHeader eyebrow={venue?.name} title="EHO audit pack" subtitle="Every record an inspector asks for, for any date range. Print or save as PDF."
          actions={data && <button data-testid="download-eho-audit-pack-button" onClick={() => window.print()} className="ka-press inline-flex items-center gap-1 rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><Printer className="h-4 w-4" />Print / Save PDF</button>} />
        <Card className="mb-8">
          <form onSubmit={build} className="flex flex-wrap items-end gap-4">
            <Field label="From"><input data-testid="audit-from-input" type="date" className="ka-input" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
            <Field label="To"><input data-testid="audit-to-input" type="date" className="ka-input" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
            <button data-testid="generate-audit-pack-button" disabled={busy} className="ka-press inline-flex h-[42px] items-center gap-2 rounded-full bg-emerald-600 px-6 text-sm font-semibold text-white hover:bg-emerald-700"><FileText className="h-4 w-4" />{busy ? "Building…" : "Build audit pack"}</button>
          </form>
        </Card>
      </div>
      {data && <Report d={data} />}
    </div>
  );
}
